// Batch 29 · M69 — the front-end safety net around every screen (Layout's <Outlet />).
// * A read answered from the last-good copy → banner "Showing the saved version of <DD-MMM-YYYY HH:mm> by <name> – live view
//   unavailable" (EN / AR) and editing off (Batch 31 · M85: writes refused, edit controls off — navigation stays on).
// * A read that has no saved copy → a clear message instead of a blank page.
// * The screen itself throws while rendering → the saved copies of the reads this page made, shown read-only (a plain
//   key / value and table view), or the "never saved" message. The failure is logged (client-errors → Founder Errors tab).
import React from "react";
import { readActiveEngagement } from "@/lib/activeEngagement";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { asArray } from "@/lib/asArray";
import { fmtDate } from "@/lib/dt";
import { FALLBACK_EVENT, SAVED_COPY_TEXT, bannerText, lockEditing, pageReads, setReadOnly, unlockEditing } from "@/lib/savedCopy";

const HIDDEN = new Set(["id", "org_id", "engagement_id", "_id", "fallback", "saved_at", "saved_by"]);
const ISO_DAY = /^\d{4}-\d{2}-\d{2}(T|$)/;

function show(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "✓" : "✗";
  if (typeof v === "string" && ISO_DAY.test(v)) return fmtDate(v) || v;
  if (typeof v === "number") return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return String(v);
}

/** A plain read-only rendering of a saved reply. */
export function SavedValue({ value, depth = 0 }) {
  if (Array.isArray(value)) {
    const rows = asArray(value);
    if (!rows.length) return <span className="text-muted-foreground">—</span>;
    if (rows.every((r) => r && typeof r === "object" && !Array.isArray(r))) {
      const cols = [];
      rows.slice(0, 50).forEach((r) => Object.keys(r).forEach((k) => {
        if (!HIDDEN.has(k) && !cols.includes(k) && cols.length < 12 && (r[k] === null || typeof r[k] !== "object")) cols.push(k);
      }));
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-[11px]" data-testid="saved-copy-table">
            <thead><tr>{cols.map((c) => <th key={c} className="border-b px-2 py-1 text-start font-medium">{c}</th>)}</tr></thead>
            <tbody>{rows.slice(0, 500).map((r, i) => (
              <tr key={i} className="border-b">{cols.map((c) => <td key={c} className="px-2 py-1 align-top">{show(r[c])}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      );
    }
    return <span>{rows.map(show).join(", ")}</span>;
  }
  if (value && typeof value === "object") {
    const keys = Object.keys(value).filter((k) => !HIDDEN.has(k));
    if (depth > 4) return <span className="text-muted-foreground">…</span>;
    return (
      <dl className={`grid grid-cols-1 gap-x-4 gap-y-1 ${depth ? "ps-3" : ""} sm:grid-cols-[minmax(160px,auto)_1fr]`}>
        {keys.map((k) => (
          <React.Fragment key={k}>
            <dt className="text-[11px] font-medium text-muted-foreground">{k}</dt>
            <dd className="text-[12px]"><SavedValue value={value[k]} depth={depth + 1} /></dd>
          </React.Fragment>
        ))}
      </dl>
    );
  }
  return <span>{show(value)}</span>;
}

export function SavedCopyBanner({ lang, savedAt, savedBy, testid = "saved-copy-banner" }) {
  const L = SAVED_COPY_TEXT[lang === "ar" ? "ar" : "en"];
  return (
    <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
         role="status" data-testid={testid}>
      <div className="font-medium" data-testid={`${testid}-text`}>{bannerText(lang, savedAt, savedBy)}</div>
      <div className="text-[11px]">{L.readOnly}</div>
    </div>
  );
}

function NeverSaved({ lang }) {
  return (
    <div className="rounded border border-slate-300 bg-slate-50 px-3 py-3 text-sm dark:bg-slate-900" role="status" data-testid="saved-copy-never">
      {SAVED_COPY_TEXT[lang === "ar" ? "ar" : "en"].neverSaved}
    </div>
  );
}

/** When the screen throws while rendering: fetch and show the saved copies of this page's reads. */
class RenderBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false, copies: null }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error, info) {
    try {
      api.post("/client-errors", { name: (error && error.name) || "Error", message: String((error && error.message) || error),
                                   pathname: window.location.pathname, component_stack: (info && info.componentStack) || "",
                                   saved_copy: true }, { _silent: true }).catch(() => {});
    } catch { /* never block */ }
    const eng = readActiveEngagement();   // Step 11d-3 · find 1: THIS tab's engagement
    const paths = pageReads().filter((p) => p.startsWith("/api/"));
    if (!eng || !paths.length) { this.setState({ copies: [] }); return; }
    api.get(`/engagements/${eng}/last-good`, { params: { paths: paths.join(",") }, _silent: true })
      .then((r) => this.setState({ copies: asArray(r.data && r.data.copies) }))
      .catch(() => this.setState({ copies: [] }));
  }
  render() {
    if (!this.state.failed) return this.props.children;
    const { lang } = this.props;
    const L = SAVED_COPY_TEXT[lang === "ar" ? "ar" : "en"];
    const copies = this.state.copies;
    if (copies === null) return <div className="text-sm text-muted-foreground" data-testid="saved-copy-loading">{L.loading}</div>;
    if (!copies.length) return <NeverSaved lang={lang} />;
    const latest = copies.reduce((a, c) => ((c.saved_at || "") > (a.saved_at || "") ? c : a), copies[0]);
    return (
      <div data-testid="saved-copy-view">
        <SavedCopyBanner lang={lang} savedAt={latest.saved_at} savedBy={latest.saved_by} />
        <p className="mb-2 text-[12px] text-muted-foreground">{L.renderFailed}</p>
        {copies.map((c) => (
          <section key={c.key} className="mb-4 rounded border p-3" data-testid="saved-copy-section">
            <SavedValue value={c.data} />
          </section>
        ))}
      </div>
    );
  }
}

export default function SavedCopyFrame({ lang = "en", children }) {
  const location = useLocation();
  const [state, setState] = React.useState(null);     // { kind: "copy" | "never", savedAt, savedBy }
  // PR 45 review L9: the saved-version view (and its read-only mode) ends when the page, its query string or the
  // engagement changes — a fresh read decides again
  const engCtx = useEngagement();
  const engId = (engCtx && engCtx.currentEngId) || "";
  React.useEffect(() => { setState(null); }, [location.pathname, location.search, engId]);
  React.useEffect(() => {
    const on = (e) => {
      const d = (e && e.detail) || {};
      if (d.page && d.page !== window.location.pathname) return;
      setState((cur) => (cur && cur.kind === "copy" && d.kind === "never" ? cur : d));
    };
    window.addEventListener(FALLBACK_EVENT, on);
    return () => window.removeEventListener(FALLBACK_EVENT, on);
  }, []);
  const copy = !!(state && state.kind === "copy");
  const rootRef = React.useRef(null);
  // Batch 31 · item 17 (M85): editing off, navigation on. Before, a disabled <fieldset> around the whole screen also froze
  // every tab, folder, "open" and Export button. Now: (1) lib/api.js refuses every write while the saved version is shown
  // (client-side, EN / AR message) — the guarantee; (2) the screen's text fields become read-only and its choices, file
  // pickers and edit buttons (save / sign / send / upload / delete …) are disabled, here and in the dialogs it opens
  // (except a sign-in / step-up dialog), kept up to date as the screen renders more; everything else stays usable.
  React.useEffect(() => {
    if (!copy) return undefined;
    const root = rootRef.current;      // the node this effect locked — unlocked by its own cleanup (react-hooks/exhaustive-deps)
    setReadOnly(true);
    const dialogs = () => Array.from(document.querySelectorAll("[role='dialog']"));   // exempt ones: lib/savedCopy isExempt
    const apply = () => { lockEditing(rootRef.current); dialogs().forEach(lockEditing); };
    apply();
    let obs = null;
    try { obs = new MutationObserver(apply); obs.observe(document.body, { childList: true, subtree: true }); } catch { /* no DOM */ }
    return () => {
      if (obs) obs.disconnect();
      setReadOnly(false);
      unlockEditing(root);
      document.querySelectorAll("[role='dialog']").forEach(unlockEditing);
    };
  }, [copy]);
  const body = <RenderBoundary key={location.pathname} lang={lang}>{children}</RenderBoundary>;
  if (!state) return body;
  if (state.kind === "never") {
    return <div data-testid="saved-copy-frame"><NeverSaved lang={lang} />{body}</div>;
  }
  return (
    <div data-testid="saved-copy-frame" data-readonly="1">
      <SavedCopyBanner lang={lang} savedAt={state.savedAt} savedBy={state.savedBy} />
      <div ref={rootRef} data-testid="saved-copy-readonly">{body}</div>
    </div>
  );
}
